import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import { useRestaurantAdminInfiniteQuery, useRestaurantAdminMutation } from 'src/apiClient';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import React, { useState } from 'react';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { OnClickButton } from 'elements/LoadingButton';
import TextField from '@mui/material/TextField';
import { useForm } from 'react-hook-form';
import superjson from 'superjson';
import { useTranslation } from 'react-i18next';
import ButtonGroup from '@mui/material/ButtonGroup';

import type { UseFormRegister } from 'react-hook-form';
import type { APIv1RestaurantAdmin } from 'src/shared-interface/ApiRestaurantAdminInterface';

type NeededUnionType<T extends any[]> = T[number];

const removeEmpty = (obj: Record<string, unknown>): Record<string, unknown> => {
    const newObj: Record<string, unknown> = {};
    Object.keys(obj).forEach((key) => {
        if (obj[key] === Object(obj[key])) newObj[key] = removeEmpty(obj[key] as Record<string, unknown>);
        else if (obj[key] !== undefined && obj[key] !== '') newObj[key] = obj[key];
    });
    return newObj;
};

export type RestaurantAdminDataGridActionComponent = React.FunctionComponent<{
    row: Record<string, unknown>;
}>;

export type RestaurantAdminDataGridUrlActionComponent<TRow = Record<string, unknown>> = React.FunctionComponent<{
    title: React.ReactNode;
    onClick: () => Promise<void>;
    disabled: boolean;
    row: TRow;
    icon?: React.ReactNode;
}>;

export type RestaurantAdminDataGridComponent<
    TRow extends Record<string, unknown> = Record<string, unknown>,
> = React.FunctionComponent<{
    data: undefined | { pages: { result: { data: TRow[]; primaryKey: keyof TRow; cursor?: { next: string } } }[] };
    actions: Array<{
        title: string | RestaurantAdminDataGridActionComponent;
        onClick: (row: unknown) => unknown;
        icon?: React.ReactNode;
    }>;
    urlActions: Array<{
        title: string;
        icon?: React.ReactNode;
        disabled?: (row: unknown) => boolean;
        action: string | ((props: { row: unknown }) => string | undefined);
        ActionComponent?: RestaurantAdminDataGridUrlActionComponent;
        actionMiddleware?: <T>(callback: () => Awaited<T>, props: { message: string }) => () => Promise<T | undefined>;
    }>;
    onSubmit: () => void;
    refetch: () => void;
    register: UseFormRegister<any>;
    fetchNextPage: () => void;
    processAction: (props: { data: { action: string; ids: unknown[] } }) => unknown;
    columns: string[];
    columnSchema: {[key: string]: { title: string; filter?: 'textStartsWith'; width?: string; fontSize?: string; hidden?: boolean } & (unknown extends React.ReactNode ? {
        transform?: (value: unknown, values: Record<string, unknown>) => React.ReactNode;
    } : {
        transform: (value: unknown, values: Record<string, unknown>) => React.ReactNode;
    });};
}>;

export const DefaultRestaurantAdminDataGridAction = (({
    title,
    disabled,
    onClick,
    icon,
    variant = 'outlined',
}: React.ComponentProps<RestaurantAdminDataGridUrlActionComponent> & { variant?: 'outlined' | 'contained' | 'text' }) => {
    return (
        <OnClickButton
            buttonProps={{
                disableElevation: true,
                disabled,
                startIcon: icon,
                sx: {
                    color: '#F91F44',
                    minWidth: '0px',
                },
                variant,
            }}
            onClick={onClick}
        >{title}</OnClickButton>
    );
}) satisfies RestaurantAdminDataGridUrlActionComponent;

const DefaultRestaurantAdminDataGrid: RestaurantAdminDataGridComponent = ({
    data,
    actions,
    urlActions,
    onSubmit,
    refetch,
    register,
    fetchNextPage,
    processAction,
    columns,
    columnSchema,
}) => {
    const { t } = useTranslation('common');

    let showFilters = false;
    return (
        <Grid container>
            <TableContainer component={Paper}>
                <form onSubmit={onSubmit}>
                    <input hidden type="submit" />
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((key) => {
                                    const schema = columnSchema[key];
                                    if (schema.hidden === true) {
                                        return null;
                                    }

                                    if (!showFilters && 'filter' in schema) {
                                        showFilters = true;
                                    }

                                    return (
                                        <TableCell key={key}>{schema.title}</TableCell>
                                    );
                                })}

                                {actions !== undefined && (
                                    <TableCell />
                                )}
                            </TableRow>
                            {showFilters && (
                                <TableRow>
                                    {columns.map((key) => (
                                        <TableCell key={key}>
                                            {'filter' in columnSchema[key] && (
                                                <TextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    inputProps={{
                                                        ...register(key),
                                                        autoComplete: 'off',
                                                    }}
                                                    size="small"
                                                    type="text"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                    ))}

                                    {actions !== undefined && (
                                        <TableCell />
                                    )}
                                </TableRow>
                            )}
                        </TableHead>
                        <TableBody>
                            {data && data.pages.map((page) => (
                                <>
                                    {page.result.data.map((row: Record<string, unknown>, index: number) => (
                                        <TableRow key={index} title="abc">
                                            {columns.map((key) => {
                                                const schema = columnSchema[key];
                                                if (schema.hidden === true) {
                                                    return null;
                                                }

                                                return (
                                                    <TableCell
                                                        key={key}
                                                    >{'transform' in schema && schema.transform ? schema.transform(row[key], row) : row[key] as React.ReactNode}</TableCell>
                                                );
                                            })}
                                            {actions !== undefined && (
                                                <TableCell>
                                                    <ButtonGroup size="small" variant="outlined">
                                                        {actions.map(({ title: Title, onClick }) => (
                                                            <>
                                                                {typeof Title === 'string' ? (
                                                                    <Button
                                                                        key={Title}
                                                                        onClick={() => onClick(row)}
                                                                    >{Title}</Button>
                                                                ) : (
                                                                    <Title row={row} />
                                                                )}
                                                            </>
                                                        ))}
                                                        {urlActions.map(({ title, action, disabled, ActionComponent = DefaultRestaurantAdminDataGridAction, actionMiddleware, icon }) => (
                                                            <ActionComponent
                                                                disabled={disabled !== undefined ? disabled(row) : false}
                                                                icon={icon}
                                                                key={title}
                                                                onClick={async () => {
                                                                    const resolvedAction = typeof action === 'string' ? action : action({ row });
                                                                    if (resolvedAction === undefined) {
                                                                        return;
                                                                    }

                                                                    if (actionMiddleware !== undefined) {
                                                                        const message = t('dataGrid.dialog.confirmAction.message', { action: resolvedAction, key: page.result.primaryKey, value: row[page.result.primaryKey] });
                                                                        await (actionMiddleware(() => processAction({ data: { action: resolvedAction, ids: [row[page.result.primaryKey]] } }), { message })());
                                                                    } else {
                                                                        await processAction({ data: { action: resolvedAction, ids: [row[page.result.primaryKey]] } });
                                                                    }

                                                                    await refetch();
                                                                }}
                                                                row={row}
                                                                title={title}
                                                            />
                                                        ))}
                                                    </ButtonGroup>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </>
                            ))}
                            {!data && (
                                <>
                                    {[1, 2, 3, 4, 5].map((key) => (
                                        <TableRow key={key}>
                                            {columns.map((key) => {
                                                return (
                                                    <TableCell
                                                        key={key}
                                                    ><Skeleton /></TableCell>
                                                );
                                            })}
                                            {actions !== undefined && (
                                                <TableCell>
                                                    <Skeleton />
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </>
                            )}
                            {data && data.pages[data.pages.length - 1].result.cursor && data.pages[data.pages.length - 1].result.cursor?.next && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + (actions !== undefined ? 1 : 0)} sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <OnClickButton
                                            buttonProps={{ color: 'primary', disableElevation: true, size: 'small', variant: 'contained' }}
                                            onClick={fetchNextPage}
                                        >
                                            {t('dataGrid.nextPage')}
                                        </OnClickButton>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </form>
            </TableContainer>
        </Grid>
    );
};

export const RestaurantAdminDataGrid = <
    Path extends keyof APIv1RestaurantAdmin['GET'],
    TColumns extends ((keyof APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]) & string)[],
>({
    actions,
    urlActions = [],
    columns,
    source,
    columnSchema,
    TableComponent = DefaultRestaurantAdminDataGrid,
}: {
    actions?: Array<{
        title: string | RestaurantAdminDataGridActionComponent;
        onClick: (row: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]) => unknown;
        icon?: React.ReactNode;
    }>;
    urlActions?: Array<{
        title: string;
        icon?: React.ReactNode;
        actionMiddleware?: <T>(callback: () => Awaited<T>, props: { message: string }) => () => Promise<T | undefined>;
        disabled?: (row: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]) => boolean;
        // @ts-expect-error
        action: APIv1RestaurantAdmin['POST'][Path]['data']['action'] | ((props: { row: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number] }) => APIv1RestaurantAdmin['POST'][Path]['data']['action'] | undefined);
        ActionComponent?: RestaurantAdminDataGridUrlActionComponent<APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]>;
    }>;
    source: {
        path: Path;
    } & (
        // @ts-expect-error
        undefined extends APIv1RestaurantAdmin['GET'][Path]['query']
            ? Record<never, never>
            // @ts-expect-error
            : { query: APIv1RestaurantAdmin['GET'][Path]['query'] }
    ) & (
        // @ts-expect-error
        undefined extends APIv1RestaurantAdmin['GET'][Path]['params']
            ? Record<never, never>
            // @ts-expect-error
            : { params: APIv1RestaurantAdmin['GET'][Path]['params'] }
    );
    columns: TColumns;
    columnSchema: {[key in NeededUnionType<TColumns>]: { title: string; filter?: 'textStartsWith'; width?: string; fontSize?: string; hidden?: boolean } & (APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number][key] extends React.ReactNode ? {
        transform?: (value: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number][key], values: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]) => React.ReactNode;
    } : {
        transform: (value: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number][key], values: APIv1RestaurantAdmin['GET'][Path]['result']['result']['data'][number]) => React.ReactNode;
    })};
    TableComponent?: RestaurantAdminDataGridComponent;
}) => {
    const [filterState, setFilterState] = useState({});
    const { register, handleSubmit } = useForm();

    const { data, fetchNextPage, refetch } = useRestaurantAdminInfiniteQuery(
        'GET',
        source.path,
        {
            // @ts-expect-error
            params: 'params' in source && source.params,
            query: {
                ...(('query' in source && source.query) ?? {}),
                ...(Object.keys(filterState).length > 0 ? {
                    filter: superjson.stringify(filterState),
                } : {}),
            },
        },
        {
            getNextPageParam: (lastPage) => {
                return ({
                    cursor: lastPage.result.cursor.next,
                });
            },
            keepPreviousData: true,
        }
    );

    const { mutateAsync: processAction } = useRestaurantAdminMutation(
        'POST',
        // @ts-expect-error
        source.path,
    );

    const onSubmit = handleSubmit((data) => {
        setFilterState(removeEmpty(data));
    });

    return (
        <TableComponent
            actions={actions ?? []}
            columns={columns}
            // @ts-expect-error
            columnSchema={columnSchema}
            // @ts-expect-error
            data={data}
            fetchNextPage={fetchNextPage}
            onSubmit={onSubmit}
            processAction={processAction}
            refetch={refetch}
            register={register}
            // @ts-expect-error
            urlActions={urlActions}
        />
    );
};
